import { useEffect, useMemo, type BaseSyntheticEvent } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { invoiceSubmitSchema, type InvoiceSubmitValues } from '../schemas/invoiceSubmit.schema';
import { mileageBelowRecordedWarning } from '../utils/mileageWarning';
import type { SubmitOutcome } from './useInvoiceSubmission';

export interface InvoiceDetailsForm {
    control: Control<InvoiceSubmitValues>;
    isSubmitting: boolean;
    mileageWarning: string | null;
    mileageBlocked: boolean;
    invoicingDealerId: string;
    submit: (event?: BaseSyntheticEvent) => Promise<void>;
}

const defaultsFor = (dealerId: string | null): InvoiceSubmitValues => ({
    invoiceNumber: '',
    invoicedAt: new Date(),
    invoicingDealerId: dealerId ?? '',
    mileage: '',
    mileageConfirmed: false,
});

/** The backend's snake_case field map (`StoreInvoiceRequest`) to this form's field names. */
const FIELD_NAME_MAP: Record<string, keyof InvoiceSubmitValues> = {
    invoice_number: 'invoiceNumber',
    invoiced_at: 'invoicedAt',
    invoicing_dealer_id: 'invoicingDealerId',
    mileage: 'mileage',
};

/** react-hook-form + Zod. `onSubmit` maps every failure shape to field errors
 *  via `setError` from the async submit handler, never from an effect. */
export function useInvoiceDetailsForm(
    defaultDealerId: string | null,
    recordedMileage: number | null,
    onSubmit: (values: InvoiceSubmitValues) => Promise<SubmitOutcome>,
    onSettled?: (outcome: SubmitOutcome) => void,
    lockedDealerId: string | null = null,
): InvoiceDetailsForm {
    const resolver = useMemo(() => zodResolver(invoiceSubmitSchema(recordedMileage)), [recordedMileage]);
    const {
        control,
        handleSubmit,
        reset,
        setError,
        setValue,
        formState,
    } = useForm<InvoiceSubmitValues>({
        resolver,
        mode: 'onTouched',
        defaultValues: defaultsFor(defaultDealerId),
    });

    const { dirtyFields, isSubmitting } = formState;
    const [mileage, mileageConfirmed, invoicingDealerId] = useWatch({
        control,
        name: ['mileage', 'mileageConfirmed', 'invoicingDealerId'],
    });
    const mileageWarning = mileageBelowRecordedWarning(mileage, recordedMileage);

    useEffect(() => {
        setValue('mileageConfirmed', false);
    }, [mileage, setValue]);

    useEffect(() => {
        if (dirtyFields.invoicingDealerId) return;
        setValue('invoicingDealerId', defaultDealerId ?? '');
    }, [defaultDealerId, dirtyFields.invoicingDealerId, setValue]);

    useEffect(() => {
        if (lockedDealerId !== null) setValue('invoicingDealerId', lockedDealerId);
    }, [lockedDealerId, setValue]);

    const submit = handleSubmit(async (values) => {
        const submitted = lockedDealerId === null ? values : { ...values, invoicingDealerId: lockedDealerId };
        const outcome = await onSubmit(submitted);
        if (outcome.ok) {
            reset(defaultsFor(defaultDealerId));
        } else {
            for (const [field, messages] of Object.entries(outcome.fieldErrors)) {
                const formField = FIELD_NAME_MAP[field];
                if (formField && messages[0]) {
                    setError(formField, { message: messages[0] });
                }
            }
        }
        onSettled?.(outcome);
    });

    const mileageBlocked = mileageWarning !== null && !mileageConfirmed;

    return { control, isSubmitting, mileageWarning, mileageBlocked, invoicingDealerId: invoicingDealerId ?? '', submit };
}
